import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    if (token) {
      // Verify token and get user data
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        setUser(response.data);
        setLoading(false);
      })
      .catch(() => {
        logout();
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  // Permission checking functions
  const canCreate = () => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    const permissions = user.permissions || 'read-only';
    return ['create', 'create-edit', 'full', 'full-access'].includes(permissions);
  };

  const canEdit = () => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    const permissions = user.permissions || 'read-only';
    return ['create-edit', 'full', 'full-access'].includes(permissions);
  };

  const canDelete = () => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    const permissions = user.permissions || 'read-only';
    return ['full', 'full-access'].includes(permissions);
  };

  const isReadOnly = () => {
    if (!user) return true;
    if (user.role === 'owner') return false;
    const permissions = user.permissions || 'read-only';
    return permissions === 'read-only' || permissions === 'read';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout,
      updateUser,
      isAuthenticated: !!user,
      canCreate,
      canEdit,
      canDelete,
      isReadOnly
    }}>
      {children}
    </AuthContext.Provider>
  );
};
